import { hashBytes64, toHex64 } from '../../model/hash';
import type { StencilRotation } from '../../model/recipe';

/** A binary silhouette. `cells` is row-major, 1 = inside the shape. */
export interface Mask {
  readonly width: number;
  readonly height: number;
  readonly cells: Uint8Array;
}

export function createMask(width: number, height: number): Mask {
  return { width, height, cells: new Uint8Array(width * height) };
}

export function maskGet(mask: Mask, x: number, y: number): number {
  if (x < 0 || y < 0 || x >= mask.width || y >= mask.height) return 0;
  return mask.cells[y * mask.width + x];
}

export function countMaskCells(mask: Mask): number {
  let count = 0;
  for (let i = 0; i < mask.cells.length; i += 1) count += mask.cells[i];
  return count;
}

/** Authoring helper: `#`, `1` and `X` mean "inside", everything else means "outside". */
export function maskFromRows(rows: readonly string[]): Mask {
  const height = rows.length;
  const width = height === 0 ? 0 : rows[0].length;
  for (const row of rows) {
    if (row.length !== width) throw new Error('All stencil rows must have equal length');
  }
  const cells = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    const row = rows[y];
    for (let x = 0; x < width; x += 1) {
      const char = row[x];
      cells[y * width + x] = char === '#' || char === '1' || char === 'X' ? 1 : 0;
    }
  }
  return { width, height, cells };
}

export function maskToRows(mask: Mask): string[] {
  const rows: string[] = [];
  for (let y = 0; y < mask.height; y += 1) {
    let row = '';
    for (let x = 0; x < mask.width; x += 1) row += mask.cells[y * mask.width + x] === 1 ? '#' : '.';
    rows.push(row);
  }
  return rows;
}

export function maskHash(mask: Mask): string {
  const bytes = new Uint8Array(mask.cells.length + 4);
  bytes[0] = (mask.width >>> 8) & 0xff;
  bytes[1] = mask.width & 0xff;
  bytes[2] = (mask.height >>> 8) & 0xff;
  bytes[3] = mask.height & 0xff;
  bytes.set(mask.cells, 4);
  return toHex64(hashBytes64(bytes));
}

export function rotateMask(mask: Mask, rotation: StencilRotation): Mask {
  if (rotation === 0) return mask;
  const swap = rotation === 90 || rotation === 270;
  const width = swap ? mask.height : mask.width;
  const height = swap ? mask.width : mask.height;
  const cells = new Uint8Array(width * height);

  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      const value = mask.cells[y * mask.width + x];
      if (value === 0) continue;
      let tx: number;
      let ty: number;
      if (rotation === 90) {
        tx = mask.height - 1 - y;
        ty = x;
      } else if (rotation === 180) {
        tx = mask.width - 1 - x;
        ty = mask.height - 1 - y;
      } else {
        tx = y;
        ty = mask.width - 1 - x;
      }
      cells[ty * width + tx] = 1;
    }
  }
  return { width, height, cells };
}

export function mirrorMask(mask: Mask, horizontal: boolean, vertical: boolean): Mask {
  if (!horizontal && !vertical) return mask;
  const cells = new Uint8Array(mask.cells.length);
  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      const sx = horizontal ? mask.width - 1 - x : x;
      const sy = vertical ? mask.height - 1 - y : y;
      cells[y * mask.width + x] = mask.cells[sy * mask.width + sx];
    }
  }
  return { width: mask.width, height: mask.height, cells };
}

export interface BoundingBox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly empty: boolean;
}

export function maskBounds(mask: Mask): BoundingBox {
  let minX = mask.width;
  let minY = mask.height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      if (mask.cells[y * mask.width + x] === 1) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { minX: 0, minY: 0, maxX: -1, maxY: -1, empty: true };
  return { minX, minY, maxX, maxY, empty: false };
}

/** Crops away empty borders so scaling always sees the shape itself. */
export function trimMask(mask: Mask): Mask {
  const bounds = maskBounds(mask);
  if (bounds.empty) return createMask(1, 1);
  const width = bounds.maxX - bounds.minX + 1;
  const height = bounds.maxY - bounds.minY + 1;
  if (width === mask.width && height === mask.height) return mask;
  const cells = new Uint8Array(width * height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      cells[y * width + x] = mask.cells[(y + bounds.minY) * mask.width + (x + bounds.minX)];
    }
  }
  return { width, height, cells };
}

/**
 * Area-coverage rescale, entirely in integer arithmetic.
 *
 * Each target cell maps to an axis-aligned rectangle of the source; the covered area is
 * accumulated in units of 1/(targetWidth * targetHeight) of a source pixel and compared
 * against `coverageThresholdPermille`. No sampling, no floats, no device-dependent canvas
 * behaviour — the same source mask always rescales to the same result.
 */
export function rescaleMask(
  mask: Mask,
  targetWidth: number,
  targetHeight: number,
  coverageThresholdPermille: number,
): Mask {
  if (targetWidth <= 0 || targetHeight <= 0) throw new RangeError('Target size must be positive');
  const cells = new Uint8Array(targetWidth * targetHeight);
  const cellArea = mask.width * mask.height; // in 1/(tw*th) source-pixel units

  for (let ty = 0; ty < targetHeight; ty += 1) {
    const y0 = ty * mask.height;
    const y1 = (ty + 1) * mask.height;
    const py0 = Math.floor(y0 / targetHeight);
    const py1 = Math.ceil(y1 / targetHeight);

    for (let tx = 0; tx < targetWidth; tx += 1) {
      const x0 = tx * mask.width;
      const x1 = (tx + 1) * mask.width;
      const px0 = Math.floor(x0 / targetWidth);
      const px1 = Math.ceil(x1 / targetWidth);

      let covered = 0;
      for (let py = py0; py < py1 && py < mask.height; py += 1) {
        const top = py * targetHeight;
        const bottom = (py + 1) * targetHeight;
        const overlapY = Math.min(y1, bottom) - Math.max(y0, top);
        if (overlapY <= 0) continue;
        const rowOffset = py * mask.width;
        for (let px = px0; px < px1 && px < mask.width; px += 1) {
          if (mask.cells[rowOffset + px] === 0) continue;
          const left = px * targetWidth;
          const right = (px + 1) * targetWidth;
          const overlapX = Math.min(x1, right) - Math.max(x0, left);
          if (overlapX <= 0) continue;
          covered += overlapX * overlapY;
        }
      }
      if (covered * 1000 >= coverageThresholdPermille * cellArea) {
        cells[ty * targetWidth + tx] = 1;
      }
    }
  }
  return { width: targetWidth, height: targetHeight, cells };
}

/**
 * Fits a mask into a grid, preserving aspect ratio and centring deterministically
 * (extra space goes to the right/bottom when it cannot be split evenly).
 */
export function fitMaskIntoGrid(
  mask: Mask,
  gridWidth: number,
  gridHeight: number,
  coverageThresholdPermille: number,
): Mask {
  const trimmed = trimMask(mask);
  let boxWidth: number;
  let boxHeight: number;
  if (trimmed.width * gridHeight <= trimmed.height * gridWidth) {
    boxHeight = gridHeight;
    boxWidth = Math.max(1, Math.floor((trimmed.width * gridHeight) / trimmed.height));
  } else {
    boxWidth = gridWidth;
    boxHeight = Math.max(1, Math.floor((trimmed.height * gridWidth) / trimmed.width));
  }
  boxWidth = Math.min(boxWidth, gridWidth);
  boxHeight = Math.min(boxHeight, gridHeight);

  const scaled = rescaleMask(trimmed, boxWidth, boxHeight, coverageThresholdPermille);
  const offsetX = Math.floor((gridWidth - boxWidth) / 2);
  const offsetY = Math.floor((gridHeight - boxHeight) / 2);

  const cells = new Uint8Array(gridWidth * gridHeight);
  for (let y = 0; y < boxHeight; y += 1) {
    for (let x = 0; x < boxWidth; x += 1) {
      if (scaled.cells[y * boxWidth + x] === 1) {
        cells[(y + offsetY) * gridWidth + (x + offsetX)] = 1;
      }
    }
  }
  return { width: gridWidth, height: gridHeight, cells };
}

export interface CleanupOptions {
  /** Connected regions smaller than this are removed as scaling noise. */
  readonly minimumRegionCells: number;
  /** Fill inactive cells with at least this many active orthogonal neighbours. */
  readonly holeFillNeighbours: number;
  /** Strip cells with fewer than this many active orthogonal neighbours. */
  readonly minimumNeighbours: number;
  /** Fixed number of erosion passes; never time based. */
  readonly noisePasses: number;
}

export const DEFAULT_CLEANUP: CleanupOptions = {
  minimumRegionCells: 4,
  holeFillNeighbours: 3,
  minimumNeighbours: 1,
  noisePasses: 1,
};

/**
 * Deterministic cleanup: strip hair-thin noise, fill accidental single-cell holes and drop
 * tiny fragments. Regions that are large enough are kept, so intentionally disconnected
 * silhouettes (for example a dotted "i") survive.
 */
export function cleanupMask(mask: Mask, options: CleanupOptions = DEFAULT_CLEANUP): Mask {
  let current = mask;
  for (let pass = 0; pass < options.noisePasses; pass += 1) {
    current = removeThinCells(current, options.minimumNeighbours);
  }
  current = fillHoles(current, options.holeFillNeighbours);
  current = removeSmallRegions(current, options.minimumRegionCells);
  return current;
}

function neighbourCount(mask: Mask, x: number, y: number): number {
  return (
    maskGet(mask, x - 1, y) +
    maskGet(mask, x + 1, y) +
    maskGet(mask, x, y - 1) +
    maskGet(mask, x, y + 1)
  );
}

function removeThinCells(mask: Mask, minimumNeighbours: number): Mask {
  if (minimumNeighbours <= 0) return mask;
  const cells = Uint8Array.from(mask.cells);
  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      if (mask.cells[y * mask.width + x] === 1 && neighbourCount(mask, x, y) < minimumNeighbours) {
        cells[y * mask.width + x] = 0;
      }
    }
  }
  return { width: mask.width, height: mask.height, cells };
}

function fillHoles(mask: Mask, requiredNeighbours: number): Mask {
  const cells = Uint8Array.from(mask.cells);
  for (let y = 0; y < mask.height; y += 1) {
    for (let x = 0; x < mask.width; x += 1) {
      if (
        mask.cells[y * mask.width + x] === 0 &&
        neighbourCount(mask, x, y) >= requiredNeighbours
      ) {
        cells[y * mask.width + x] = 1;
      }
    }
  }
  return { width: mask.width, height: mask.height, cells };
}

export function findRegions(mask: Mask): { labels: Int32Array; sizes: number[] } {
  const labels = new Int32Array(mask.cells.length).fill(-1);
  const sizes: number[] = [];
  const stack: number[] = [];

  for (let start = 0; start < mask.cells.length; start += 1) {
    if (mask.cells[start] === 0 || labels[start] !== -1) continue;
    const label = sizes.length;
    let size = 0;
    stack.push(start);
    labels[start] = label;
    while (stack.length > 0) {
      const index = stack.pop() as number;
      size += 1;
      const x = index % mask.width;
      const y = (index - x) / mask.width;
      const neighbours = [
        x > 0 ? index - 1 : -1,
        x + 1 < mask.width ? index + 1 : -1,
        y > 0 ? index - mask.width : -1,
        y + 1 < mask.height ? index + mask.width : -1,
      ];
      for (const next of neighbours) {
        if (next >= 0 && mask.cells[next] === 1 && labels[next] === -1) {
          labels[next] = label;
          stack.push(next);
        }
      }
    }
    sizes.push(size);
  }
  return { labels, sizes };
}

function removeSmallRegions(mask: Mask, minimumRegionCells: number): Mask {
  if (minimumRegionCells <= 1) return mask;
  const { labels, sizes } = findRegions(mask);
  if (sizes.length === 0) return mask;

  const largest = Math.max(...sizes);
  const cells = Uint8Array.from(mask.cells);
  for (let i = 0; i < cells.length; i += 1) {
    const label = labels[i];
    if (label >= 0 && sizes[label] < minimumRegionCells && sizes[label] < largest) {
      cells[i] = 0;
    }
  }
  return { width: mask.width, height: mask.height, cells };
}
